<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading">
    <div class="header-section">
      <div class="step-label">{{ $t('barcode_recycle') }} - {{ $t('Use RFID') }}</div>
    </div>

    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
      </span>
    </div>

    <div class="barcode-list-container">
      <div v-if="scannedBarcodeList.length === 0" class="empty-state">
        <IconRfid class="empty-icon" />
        <p>{{ $t('Scan RFID tags to add barcodes') }}</p>
      </div>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList" />
    </div>

    <div class="button-container">
      <el-button class="back-button" @click="goBack">
        {{ $t('Back') }}
      </el-button>
      <el-button type="primary" class="submit-button" :disabled="scannedBarcodeList.length === 0" @click="submit()">
        {{ $t('OK') }}
      </el-button>
    </div>
  </div>

  <el-dialog
    center
    :lock-scroll="true"
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList || []" />
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('Done') }}
        </el-button>
      </span>
    </template>
  </el-dialog>

  <RfidItemSelector v-model:is-show="showRfidScanDialog" mode="multiple-select" @confirm="onRfidScanConfirmed" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { RECYCLE_MULTIPLE_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IBarcodeDefinitionType, IProject, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { recycleMultipleBarcode, getBarcode } from '@/utils/api'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'BulkRecycleRfid',
})
export default class BulkRecycleRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  get barcode(): IBarcode {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get childrenCount() {
    return this.barcode?.childrenCount || 0
  }

  get parentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  created() {
    // Initialize with the first barcode if needed
    if (this.$store.state.barcode.barcode) {
      // No need to add the initial barcode for recycle like tracking does
    }
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 0) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  barcodeTypeInfo(barcodeType: string): IBarcodeDefinitionType {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  isCurrentRecycleableTrackpoint(barcode: IBarcode): boolean {
    return (this.barcodeTypeInfo(barcode.barcodeType)?.barcodeRecyclableTrackPoints ?? []).includes(
      this.currentTrackPointKey
    )
  }

  hasParent(barcode: IBarcode): boolean {
    return barcode?.hasParent
  }

  isAllowRecycle(barcode: IBarcode): { message: string } {
    const error = { message: '' }
    if (!this.isCurrentRecycleableTrackpoint(barcode)) {
      error.message = 'cant_recycle_at_this_trackpoint'
    }

    if (this.childrenCount > 0) {
      if (!this.project.isRecycleChildren || this.hasParent(barcode)) {
        error.message = 'errors.3025'
      }
    }

    if (this.parentBarcodeId === barcode?.id) {
      error.message = 'barcode_pairing_by_itself_error'
    }

    if (!barcode?.barcodeType) {
      error.message = 'barcode not activated'
    }
    return error
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  async validateBarcode(resultScanned: IBarcode) {
    const error = this.isAllowRecycle(resultScanned)
    if (error.message) {
      throw this.$t(error.message)
    }
    const data = {
      barcodeIds: [resultScanned?.id],
      isDryRun: true,
    }
    await recycleMultipleBarcode(data)
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    if (!this.scannedBarcodeList || this.scannedBarcodeList.length === 0) {
      return
    }
    const listBarcodeIds = [
      this.$route.params.barcodeId,
      ...this.scannedBarcodeList.map((barcode: IBarcode) => barcode.id),
    ]
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.scannedBarcodeList)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('recycle_read_result'),
        operation: RECYCLE_MULTIPLE_BARCODE,
        extParams: JSON.stringify(listBarcodeIds),
      },
    })
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.header-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 25px;

  .back-button {
    background-color: #525252;
    flex-shrink: 0;
    padding: 8px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .step-label {
    flex: 1;
    margin-bottom: 0;
  }
}

.step-label {
  margin-bottom: 25px;
}

.barcode-number-block {
  margin-top: 20px;
  margin-bottom: 20px;
}

.barcode-list-container {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 20px;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  :deep(.back-button),
  :deep(.submit-button) {
    flex: 1 1 50%;
    min-width: 0;
    width: 50%;
    max-width: 50%;
    height: 40px !important;
    padding: 12px 20px !important;
    font-size: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    margin: 0 !important;
    vertical-align: middle !important;
    box-sizing: border-box !important;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;

  .empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
  }
}
</style>
